/**
 * StocksPanel — today's hot / winners / losers + optional symbol analysis.
 * Movers load from MiEMarketApi GET /api/stocks/movers (Yahoo screener proxy).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormInput,
  CRow,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react';
import config from '../../../utils/EnvironmentSwitcher';

const formatPrice = (n) =>
  n == null || !Number.isFinite(Number(n)) ? '—' : `$${Number(n).toFixed(2)}`;

const formatPct = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
};

const formatVolume = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(Math.round(v));
};

const normalizeAnalysis = (raw) => {
  if (!raw) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') {
    if (raw.data != null && typeof raw.data !== 'object') return String(raw.data);
    return JSON.stringify(raw.data ?? raw, null, 2);
  }
  return String(raw);
};

const MoversTable = ({ title, rows, accent }) => (
  <CCard className="mb-4 crypto-card h-100">
    <CCardHeader className="d-flex justify-content-between align-items-center">
      <h5 className="mb-0">{title}</h5>
      <CBadge color={accent}>{rows.length}</CBadge>
    </CCardHeader>
    <CCardBody className="p-0">
      <CTable hover responsive className="crypto-table mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell>Symbol</CTableHeaderCell>
            <CTableHeaderCell>Name</CTableHeaderCell>
            <CTableHeaderCell>Price</CTableHeaderCell>
            <CTableHeaderCell>Change</CTableHeaderCell>
            <CTableHeaderCell>Volume</CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {rows.map((row) => {
            const up = Number(row.changePercent) >= 0;
            return (
              <CTableRow key={row.symbol}>
                <CTableDataCell>
                  <strong>{row.symbol}</strong>
                </CTableDataCell>
                <CTableDataCell style={{ maxWidth: 160 }} className="text-truncate">
                  {row.name}
                </CTableDataCell>
                <CTableDataCell>{formatPrice(row.price)}</CTableDataCell>
                <CTableDataCell>
                  <CBadge color={up ? 'success' : 'danger'}>{formatPct(row.changePercent)}</CBadge>
                </CTableDataCell>
                <CTableDataCell>{formatVolume(row.volume)}</CTableDataCell>
              </CTableRow>
            );
          })}
          {!rows.length && (
            <CTableRow>
              <CTableDataCell colSpan={5} className="text-center py-3">
                No quotes available.
              </CTableDataCell>
            </CTableRow>
          )}
        </CTableBody>
      </CTable>
    </CCardBody>
  </CCard>
);

const StocksPanel = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [asOf, setAsOf] = useState(null);
  const [hot, setHot] = useState([]);
  const [winners, setWinners] = useState([]);
  const [losers, setLosers] = useState([]);

  const [symbols, setSymbols] = useState('AAPL,MSFT,NVDA,TSLA');
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState('');
  const [analysisResult, setAnalysisResult] = useState('');

  const analysisText = useMemo(() => normalizeAnalysis(analysisResult), [analysisResult]);

  const fetchMovers = useCallback(async ({ isRefresh = false } = {}) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const res = await fetch(`${config.apiUrl}/api/stocks/movers?count=12`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Movers request failed (${res.status})`);
      }
      const data = await res.json();
      setHot(Array.isArray(data.hot) ? data.hot : []);
      setWinners(Array.isArray(data.winners) ? data.winners : []);
      setLosers(Array.isArray(data.losers) ? data.losers : []);
      setAsOf(data.asOf || new Date().toISOString());
    } catch (err) {
      console.error('StocksPanel movers error:', err);
      setError(err.message || 'Failed to load stock movers');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchMovers();
  }, [fetchMovers]);

  const handleAnalyze = async (e) => {
    e.preventDefault();
    setAnalysisLoading(true);
    setAnalysisError('');
    setAnalysisResult('');
    try {
      // Prefer API proxy; fall back to direct stocks.mieros.cloud if route not deployed yet
      let res = await fetch(`${config.apiUrl}/api/stocks/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbols }),
      });
      if (res.status === 404) {
        res = await fetch('https://stocks.mieros.cloud', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ symbols }),
        });
      }
      if (!res.ok) throw new Error(`Analysis failed (${res.status})`);
      const contentType = res.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await res.json() : await res.text();
      setAnalysisResult(data);
    } catch (err) {
      console.error('Stock analysis error:', err);
      setAnalysisError(err.message || 'Error fetching stock analysis');
    } finally {
      setAnalysisLoading(false);
    }
  };

  if (loading) {
    return (
      <CCard className="text-center p-4">
        <div className="d-flex justify-content-center align-items-center gap-2">
          <CSpinner color="primary" />
          <span>Loading today&apos;s stock movers...</span>
        </div>
      </CCard>
    );
  }

  return (
    <div className="stocks-panel">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h3 className="mb-1">US Stocks — Today</h3>
          {asOf && (
            <small className="mb-0" style={{ color: 'var(--text-secondary)' }}>
              As of {new Date(asOf).toLocaleString()}
            </small>
          )}
        </div>
        <CButton
          color="info"
          variant="outline"
          onClick={() => fetchMovers({ isRefresh: true })}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing...' : 'Refresh Movers'}
        </CButton>
      </div>

      {error && (
        <CAlert color="warning" className="mb-4">
          {error}
          <div className="mt-1 small">
            Ensure MiEMarketApi exposes <code>GET /api/stocks/movers</code> (rebuild API if needed).
          </div>
        </CAlert>
      )}

      <CRow>
        <CCol lg={4} className="mb-4">
          <MoversTable title="Hot (Most Active)" rows={hot} accent="info" />
        </CCol>
        <CCol lg={4} className="mb-4">
          <MoversTable title="Winners" rows={winners} accent="success" />
        </CCol>
        <CCol lg={4} className="mb-4">
          <MoversTable title="Losers" rows={losers} accent="danger" />
        </CCol>
      </CRow>

      <CCard className="mb-4 stock-analysis-card">
        <CCardHeader>
          <h5 className="mb-0">Stock Analysis</h5>
        </CCardHeader>
        <CCardBody>
          <form onSubmit={handleAnalyze} className="d-flex gap-2 flex-wrap align-items-center">
            <label htmlFor="stock-symbols" className="mb-0">
              Symbols
            </label>
            <CFormInput
              type="text"
              id="stock-symbols"
              value={symbols}
              onChange={(e) => setSymbols(e.target.value)}
              placeholder="e.g. AAPL,MSFT,TSLA"
              required
              style={{ minWidth: 260, maxWidth: 420 }}
            />
            <CButton type="submit" color="primary" disabled={analysisLoading}>
              {analysisLoading ? 'Analyzing...' : 'Analyze'}
            </CButton>
          </form>

          {analysisLoading && (
            <div className="mt-3">
              <CSpinner size="sm" className="me-2" />
              <span>Running stock analysis...</span>
            </div>
          )}
          {analysisError && (
            <CAlert color="danger" className="mt-3 mb-0">
              {analysisError}
            </CAlert>
          )}
          {analysisText && (
            <pre
              className="mt-3 mb-0 p-3"
              style={{
                borderRadius: 10,
                border: '1px solid var(--border-subtle)',
                background: 'rgba(255,255,255,0.03)',
                color: 'var(--text-secondary)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {analysisText}
            </pre>
          )}
        </CCardBody>
      </CCard>
    </div>
  );
};

export default StocksPanel;
